/**
 * "Set up your cabinet" builder for Horn cabinets on the product page.
 *
 * Owns the inset choice, the machine make/model field, the postcode-gated
 * assembly service and the running total. Same look as the bundles builder
 * (see assets/bundle-offers.js).
 *
 * Cart contract (read by assets/product-form.js, unchanged):
 *   input[name="insert_variant"]        chosen inset variant ID or ""
 *   input[name="insert_machine_model"]  customer's machine make/model
 *   input[name="assembly_variant"]      assembly service variant ID or ""
 *   input[name="properties[Machine and Model Number]"] carries `required`
 *     when an inset needs it; product-form.js reveals the sibling
 *     .horn-cabinets-form-fields__error if it is left empty.
 */
class HornSetup extends HTMLElement {
  connectedCallback() {
    const form = document.querySelector('product-form');
    this.insertVariantInput = form ? form.querySelector('input[name="insert_variant"]') : null;
    this.insertModelInput = form ? form.querySelector('input[name="insert_machine_model"]') : null;
    this.assemblyVariantInput = form ? form.querySelector('input[name="assembly_variant"]') : null;

    this.cabinetPrice = parseInt(this.dataset.cabinetPrice, 10) || 0;
    this.assemblyPrice = parseInt(this.dataset.assemblyPrice, 10) || 0;
    this.assemblyVariant = this.dataset.assemblyVariant || '';
    this.moneyFormat = this.dataset.moneyFormat || '£{{amount}}';
    this.freeInsert = this.dataset.freeInsert === 'true';

    this.insetOptions = Array.from(this.querySelectorAll('[data-horn-insert-option]'));
    this.modelField = this.querySelector('[data-horn-model-field]');
    this.modelInput = this.querySelector('[data-horn-model-input]');
    this.modelError = this.querySelector('.horn-cabinets-form-fields__error');

    this.assemblyRow = this.querySelector('[data-horn-assembly-row]');
    this.assemblyToggle = this.querySelector('[data-horn-assembly-toggle]');
    this.postcodeInput = this.querySelector('[data-horn-postcode]');
    this.postcodeCheck = this.querySelector('[data-horn-postcode-check]');
    this.zoneOk = this.querySelector('[data-horn-zone-ok]');
    this.zoneNo = this.querySelector('[data-horn-zone-no]');
    this.zoneInvalid = this.querySelector('[data-horn-zone-invalid]');

    this.summaryLabel = this.querySelector('[data-horn-summary-label]');
    this.summaryTotal = this.querySelector('[data-horn-summary-total]');

    this.insetOptions.forEach((opt) => {
      opt.addEventListener('click', (e) => {
        if (e.target.closest('a[href]')) return;
        this.selectInset(opt);
      });
      opt.addEventListener('keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        this.selectInset(opt);
      });
    });

    if (this.modelInput) {
      this.modelInput.addEventListener('input', () => {
        if (this.insertModelInput) this.insertModelInput.value = this.modelInput.value;
        if (this.modelError && this.modelInput.value.trim() !== '') this.modelError.hidden = true;
      });
    }

    if (this.assemblyToggle) {
      this.assemblyToggle.addEventListener('click', () => this.toggleAssembly());
    }
    if (this.postcodeCheck) {
      this.postcodeCheck.addEventListener('click', () => this.checkPostcode());
    }
    if (this.postcodeInput) {
      this.postcodeInput.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this.checkPostcode();
      });
    }

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        if (variant && typeof variant.price === 'number') {
          this.cabinetPrice = variant.price;
          this.updateSummary();
        }
      });
    }

    // Reflect the pre-selected inset row (No inset) into the hidden inputs.
    const selected = this.insetOptions.find((o) => o.classList.contains('selected_include'));
    if (selected) this.applyInset(selected);
    this.updateSummary();
  }

  disconnectedCallback() {
    if (this.variantUnsubscribe) this.variantUnsubscribe();
  }

  /* ---------- inset ---------- */

  selectInset(option) {
    this.insetOptions.forEach((o) => {
      const on = o === option;
      o.classList.toggle('selected_include', on);
      o.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    this.applyInset(option);
    this.updateSummary();
  }

  applyInset(option) {
    const variantId = option.dataset.variantId || '';
    if (this.insertVariantInput) this.insertVariantInput.value = variantId;

    const needsModel = this.freeInsert || variantId !== '';
    if (this.modelField) {
      this.modelField.hidden = !needsModel;
      if (this.modelInput) {
        if (needsModel) {
          this.modelInput.setAttribute('required', 'required');
        } else {
          this.modelInput.removeAttribute('required');
          this.modelInput.value = '';
          if (this.insertModelInput) this.insertModelInput.value = '';
          if (this.modelError) this.modelError.hidden = true;
        }
      }
    }
  }

  selectedInsetPrice() {
    const selected = this.insetOptions.find((o) => o.classList.contains('selected_include'));
    return selected ? parseInt(selected.dataset.price, 10) || 0 : 0;
  }

  /* ---------- assembly ---------- */

  // Zone C postcodes: outside carriers, no pre-assembled delivery.
  static get zoneCPatterns() {
    return [
      /^AB(3[1-8]|4[0-9]|5[0-6])/i,
      /^IV\d/i,
      /^KA(27|28)/i,
      /^KW\d/i,
      /^PA(2[0-9]|[3-7][0-9]|78)/i,
      /^PH([4-9]|[1-4][0-9]|50)\D/i,
      /^ZE\d/i,
      /^PO(3[0-9]|4[01])/i,
      /^IM\d/i,
      /^BT\d/i,
      /^GY\d/i,
      /^JE\d/i,
    ];
  }

  checkPostcode() {
    if (!this.postcodeInput) return;
    const raw = this.postcodeInput.value.trim();
    const compact = raw.replace(/\s+/g, '').toUpperCase();
    const valid = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(compact) || /^[A-Z]{1,2}\d[A-Z\d]?$/.test(compact);

    this.zoneInvalid.hidden = valid;
    if (!valid) {
      this.zoneOk.hidden = true;
      this.zoneNo.hidden = true;
      this.setAssemblyAvailable(false);
      return;
    }

    const zoneC = HornSetup.zoneCPatterns.some((re) => re.test(compact + ' '));
    this.zoneOk.hidden = zoneC;
    this.zoneNo.hidden = !zoneC;
    this.setAssemblyAvailable(!zoneC);
  }

  setAssemblyAvailable(available) {
    if (!this.assemblyToggle) return;
    this.assemblyToggle.disabled = !available;
    if (!available) this.setAssembly(false);
    if (this.assemblyRow) this.assemblyRow.dataset.state = available ? (this.assemblyOn ? 'added' : 'available') : 'unchecked';
  }

  toggleAssembly() {
    this.setAssembly(!this.assemblyOn);
    this.updateSummary();
  }

  setAssembly(on) {
    this.assemblyOn = !!on;
    if (this.assemblyVariantInput) this.assemblyVariantInput.value = this.assemblyOn ? this.assemblyVariant : '';
    if (this.assemblyToggle) this.assemblyToggle.setAttribute('aria-pressed', this.assemblyOn ? 'true' : 'false');
    if (this.assemblyRow) {
      this.assemblyRow.classList.toggle('selected', this.assemblyOn);
      if (!this.assemblyToggle.disabled) this.assemblyRow.dataset.state = this.assemblyOn ? 'added' : 'available';
    }
  }

  /* ---------- summary ---------- */

  updateSummary() {
    if (!this.summaryTotal) return;
    const inset = this.selectedInsetPrice();
    const assembly = this.assemblyOn ? this.assemblyPrice : 0;
    const parts = ['Cabinet'];
    if (inset > 0) parts.push('inset');
    if (assembly > 0) parts.push('assembly');
    this.summaryLabel.textContent = parts.length === 1 ? 'Cabinet only' : parts.join(' + ');
    this.summaryTotal.textContent = this.formatMoney(this.cabinetPrice + inset + assembly);
    this.classList.toggle('setup-builder--has-extras', parts.length > 1);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, withCommas)
      .replace(/<[^>]+>/g, '');
  }
}

if (!customElements.get('horn-setup')) {
  customElements.define('horn-setup', HornSetup);
}
